import { ADToBS } from "bikram-sambat-js";
import { formatNumWithCommas } from "utils/formatString";
import nctLogo from "../../../../../assets/nctLogo2.png";

// Utility to convert number to words
const numberToWords = (num) => {
  const integerPart = Math.floor(num);
  const decimalPart = Math.round((num - integerPart) * 100); // Get cents

  const units = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const thousands = ["", "Thousand", "Million"];
  const convertLessThanThousand = (num) => {
    if (num === 0) return "";
    if (num < 20) return units[num];
    if (num < 100) {
      return (
        tens[Math.floor(num / 10)] + (num % 10 ? " " + units[num % 10] : "")
      );
    }
    return (
      units[Math.floor(num / 100)] +
      " Hundred" +
      (num % 100 ? " " + convertLessThanThousand(num % 100) : "")
    );
  };

  let word = "";
  let thousandIndex = 0;
  let tempNum = integerPart;

  if (tempNum === 0) {
    word = "Zero";
  } else {
    while (tempNum > 0) {
      if (tempNum % 1000 !== 0) {
        word =
          convertLessThanThousand(tempNum % 1000) +
          (thousands[thousandIndex] ? " " + thousands[thousandIndex] : "") +
          (word ? " " + word : "");
      }
      tempNum = Math.floor(tempNum / 1000);
      thousandIndex++;
    }
  }

  if (decimalPart > 0) {
    word += " and " + convertLessThanThousand(decimalPart) + " Cents";
  }

  return word || "Zero";
};

// Error boundary component

// Main invoice component
const ShipmentInvoicePrint = () => { };

// Static print method
ShipmentInvoicePrint.printStatic = ({
  shipment,
  pricesRes,
  loggedInUser,
  entity,
}) => {
  try {
    const printWindow = window.open("", "", "width=900,height=700");
    if (!printWindow) {
      console.error("Failed to open static print window");
      return;
    }

    const getWeightLabel = (s) => {
      if (s?.weight) return `${s.weight} Kg`;
      if (s?.unit?.value && s?.unit?.unitType?.name) {
        return s.unit.unitType.name.toLowerCase() === "weight"
          ? `${s.unit.value} Kg`
          : `${s.unit.value} ${s.unit.unitType.name}`;
      }
      return "N/A";
    };
    const getWeightValue = (s) => {
      if (s?.weight) return s.weight;
      if (s?.unit?.value) return s.unit.value;
      return 1;
    };

    // Prepare dynamic data for static HTML
    const pickupTime = shipment?.shipmentPickupTime
      ? new Date(shipment.shipmentPickupTime)
      : new Date();
    const formatDate = (date) => {
      const adDateString = date.toLocaleDateString("en-CA").replace(/-/g, "/"); // Format as YYYY/MM/DD
      const bsDate = ADToBS(adDateString); // Convert to BS date
      return {
        miti: bsDate,
        time: date.toLocaleTimeString("en-GB", { hour12: false }),
        date: date.toLocaleDateString("en-GB").split("/").reverse().join("/"),
      };
    };
    const { miti, time, date } = formatDate(pickupTime);

    const showVatNumber =
      shipment?.entity?.additionalInfo?.vatNumber?.display !== false;

    // Determine sender: use customerId if available, else vendor
    const sender = shipment?.customerId
      ? {
        name: shipment?.customerId?.customerUser?.name || "--",
      }
      : {
        name: shipment?.vendor?.shopName || "--",
      };

    const VAT_RATE = 0.13;
    const roundMoney = (value) => Number(Number(value || 0).toFixed(2));
    const getDescription = (item) => {
      const name = item?.feeItem?.name || "Shipment Charge";
      if (name === "BASE - Standard charge") return "Service Charge";
      if (name.toLowerCase().includes("freight")) return "Freight Charge";
      if (name.toLowerCase().includes("documentation")) return "Documentation & Processing Fee";
      return name;
    };
    const particulars = pricesRes?.data?.length
      ? pricesRes.data
        .filter((item) => item.chargeType !== "Cash on Delivery")
        .map((item) => ({
          description: getDescription(item),
          amount: roundMoney(item.charge),
          rate: roundMoney(Number(item.charge || 0) / (1 + VAT_RATE)),
          vat: roundMoney(Number(item.charge || 0) - roundMoney(Number(item.charge || 0) / (1 + VAT_RATE))),
        }))
      : [
        {
          description: "Shipment Charge",
          rate: 0.0,
          amount: 0.0,
        },
      ];

    printWindow.document.open();
    printWindow.document.write(`
     <html>
      <head>
        <title>Shipment Sales Receipt</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            font-size: 14px;
            margin: 10px;
            padding: 20px;
          }

          .container {
            width: 100%;
            margin: 0 auto;
            padding: 8px;
            box-sizing: border-box;
          }

          .flex {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 13px;
          }

          th,
          td {
            padding: 4px;
          }

          th {
            text-align: left;
            border-bottom: 1px solid #000;
            border-top: 1px solid #000;
          }

          td {
            border-bottom: 1px solid #000;
          }

          .right {
            text-align: right;
          }

          .center {
            text-align: center;
          }

          .total-row {
            border-top: 1px solid #000;
          }

          hr {
            margin: 8px 0;
            border: 0;
            border-top: 1px dashed #000;
          }

          @media print {
            body {
              margin: 0;
              padding: 20px;
            }

            .container {
              margin: 0;
              padding: 20px;
            }

            @page {
              size: landscape;
              margin: 0.5cm;
            }
          }
        </style>
      </head>

      <body>
        <div class="container">
          <!-- Header Section -->
          <div class="flex" style="display: flex; align-items: center;">
            <!-- Logo on the left -->
            <div style="display: flex; align-items: flex-start; width: 100%;">
              <!-- Logo (Left) -->
              <div style="flex: 1 0 auto; justify-content: center; align-items: center;">
                <div style="display: flex; align-items: center; margin-bottom: 8px;">
                  <img src="${entity?.logo?.url || nctLogo
      }" alt="Logo" style="height: 80px;" />
                  <div style="display: flex; flex-direction: column; margin-left: 10px;">
                    <div style="font-size: 16px; font-weight: bold; color: #e53935;">
                      ${entity?.name || ""}
                    </div>
                    <div style="font-size: 14px;">${entity?.location || ""
      }</div>
                  </div>
                </div>
              </div>

              <div style="text-align: right; font-size: 12px; margin-top: 10px; flex: 1;">
                <div>Tel No.: ${entity?.telNo || ""}</div>
                ${showVatNumber
        ? `<div>VAT No.: ${entity?.additionalInfo?.vatNumber?.value || ""
        }</div>`
        : ""
      }
              </div>
            </div>
          </div>

          <div style="text-align: center; margin-top: 10px;">
            <h2 style="display: inline-block; font-size: 16px;">Sales Receipt</h2>
          </div>

          <hr />

          <!-- First Details Section -->
          <table style="width: 100%;">
            <tr>
              <td style="border: none;"><strong>Receipt No:</strong> ${shipment?.receiptNumber || ""
      }</td>
              <td style="border: none;"><strong>Shipment ID:</strong> ${shipment?.shipmentId || ""
      }</td>
              <td style="border: none;"><strong>Mode:</strong> To Pay</td>
              <td style="border: none;"><strong>Miti:</strong> ${miti}</td>
              <td style="border: none;"><strong>Time:</strong> ${time}</td>
            </tr>
            <tr>
              <td style="border: none;"><strong>Customer:</strong> ${sender?.name || "--"
      }</td>
              ${showVatNumber
        ? `<td style="border: none;"><strong>VAT No:</strong> </td>`
        : `<td style="border: none;"></td>`
      }
              <td style="border: none;"><strong>Date:</strong> ${date}</td>
              <td style="border: none;"></td>
            </tr>
          </table>

          <hr />

          <!-- Second Details Section -->
          <table style="width: 100%; border-collapse: collapse;">
            <tr style="border: none;">
              <td style="border: none;"><strong>Sender:</strong> ${sender.name
      }</td>
              ${showVatNumber
        ? `<td style="border: none;"><strong>VAT No:</strong> </td>`
        : `<td style="border: none;"></td>`
      }
              <td style="border: none;"><strong>From:</strong> ${shipment?.sourceBranch?.name || "--"
      }</td>
              <td style="border: none;"></td>
            </tr>
            <tr style="border: none;">
              <td style="border: none;"><strong>Receiver:</strong> ${shipment?.receiver?.customerUser?.name || "Nepal Can Transport"
      }</td>
              <td style="border: none;"></td>
              <td style="border: none;"><strong>To:</strong> ${shipment?.destinationBranch?.name || "--"
      }</td>
              <td style="border: none;"></td>
            </tr>
            <tr style="border: none;">
              <td style="border: none;"><strong>Phone:</strong> ${shipment?.receiver?.phone || ""
      }</td>
              <td style="border: none;"><strong>Address:</strong> ${shipment?.receiver?.address || "--"
      }</td>
              <td style="border: none;"><strong>G.Value:</strong></td>
            </tr>
          </table>

          <!-- Product Table -->
          <table>
            <thead>
              <tr>
                <th>SN.</th>
                <th>Particular</th>
<th>${shipment?.unit?.value && shipment?.unit?.unitType?.name?.toLowerCase() !== "weight" ? "Unit" : "Weight"}</th>
                <th>Rate</th>
                <th>Taxable Amt</th>
                <th>VAT 13%</th>
                <th class="right">Total Amt</th>
              </tr>
            </thead>
            <tbody>
              ${particulars
        .map(
          (item, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${item?.description || "Shipment Charge"}</td>
<td>${index === 0 ? getWeightLabel(shipment) : ""}</td>
                  <td>Rs. ${formatNumWithCommas(
            (item?.rate / getWeightValue(shipment)).toFixed(2) || "0.00"
          )}</td>
                  <td>Rs. ${formatNumWithCommas(item?.rate?.toFixed(2)) || "0.00"
            }</td>
                  <td>Rs. ${formatNumWithCommas(item?.vat?.toFixed(2)) || "0.00"
            }</td>
                  <td class="right">Rs. ${formatNumWithCommas(item?.amount?.toFixed(2)) || "0.00"
            }</td>
                  
                </tr>`
        )
        .join("")}
              <tr class="total-row">
                <td colspan="6" style="border: none;"><strong>Total</strong></td>
               
                <td class="right">
                  <strong>Rs. ${formatNumWithCommas(
          particulars
            .reduce((sum, item) => sum + item?.amount, 0)
            ?.toFixed(2)
        ) || "0.00"
      }</strong>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Charges Section -->
      <div style="margin: 8px 0;">
        <hr />
            <p class="right">Total Taxable Amount: <strong>Rs. ${formatNumWithCommas(particulars.reduce((sum, item) => sum + item.rate, 0).toFixed(2))}</strong></p>
            <p class="right">VAT 13%: <strong>Rs. ${formatNumWithCommas(particulars.reduce((sum, item) => sum + item.vat, 0).toFixed(2))}</strong></p>
            <p class="right" style="font-size: 15px;">Grand Total: <strong>Rs. ${formatNumWithCommas(particulars.reduce((sum, item) => sum + item.amount, 0).toFixed(2))}</strong></p>
            <p style="font-size: 11px;">VAT-inclusive amounts are back-calculated using: <strong>Base Amount = Original Amount ÷ 1.13</strong>. Base Amount + VAT = Original Amount for each line.</p>
          </div>

          <!-- In Words and Remarks -->
          <div style="margin-bottom: 8px;">
            <p>
              <strong>In words:</strong>
              ${numberToWords(
        Math.round(
          particulars.reduce((sum, item) => sum + item?.amount, 0)
        )
      )} Rupees Only.
            </p>
            <hr />
            <p><strong>Remarks:</strong></p>
            <p>${shipment?.remarks || ""}</p>
          </div>

          <!-- Footer -->
          <div class="flex footer">
            <div></div>
            <div class="right">
              <p>
                <strong>Print Time:</strong>
                ${new Date().toLocaleString("en-US", {
        dateStyle: "medium",
        timeStyle: "medium",
      })}
                <br />
                <div style="margin-top: 2px;">
                  <strong>Booking Officer:</strong> ${loggedInUser?.name || "N/A"
      }
                </div>
              </p>
            </div>
          </div>

          <div style="text-align: center; margin-top: 20px; font-size: 11px;">
            <p>
              Thank you for choosing ${entity?.name || " "
      } as your logistics partner!
            </p>
          </div>
        </div>
      </body>
    </html>

    `);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.document.title = "Shipment Receipt";
      printWindow.print();
      printWindow.close();
    }, 1000);
  } catch (error) {
    console.error("Error printing static receipt:", error);
  }
};

export default ShipmentInvoicePrint;
